import { ReactNode, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import {
  HeaderFiltersContext,
  type FilterConfig,
  type HeaderSearchConfig,
  type HeaderFiltersContextValue,
  type PathConfigs
} from './types';

interface HeaderFiltersProviderProps {
  children: ReactNode;
}

export function HeaderFiltersProvider({ children }: HeaderFiltersProviderProps) {
  const location = useLocation();
  const [pathConfigs, setPathConfigs] = useState<PathConfigs>({});
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const pathKey = location.pathname;

  const currentConfigs = useMemo(() => {
    return pathConfigs[pathKey] || { filterConfig: null, searchConfig: null };
  }, [pathConfigs, pathKey]);

  const setFilterConfig = useCallback(
    (config: FilterConfig | null) => {
      setPathConfigs((prev) => ({
        ...prev,
        [pathKey]: {
          ...prev[pathKey],
          filterConfig: config
        }
      }));
    },
    [pathKey]
  );

  const setSearchConfig = useCallback(
    (config: HeaderSearchConfig | null) => {
      setPathConfigs((prev) => ({
        ...prev,
        [pathKey]: {
          ...prev[pathKey],
          searchConfig: config
        }
      }));
    },
    [pathKey]
  );

  const openFilters = useCallback(() => setIsFilterOpen(true), []);
  const closeFilters = useCallback(() => setIsFilterOpen(false), []);
  const toggleFilters = useCallback(() => setIsFilterOpen((prev) => !prev), []);

  const value: HeaderFiltersContextValue = {
    filterConfig: currentConfigs.filterConfig,
    searchConfig: currentConfigs.searchConfig,
    isFilterOpen,
    setFilterConfig,
    setSearchConfig,
    openFilters,
    closeFilters,
    toggleFilters
  };

  return <HeaderFiltersContext.Provider value={value}>{children}</HeaderFiltersContext.Provider>;
}
